import React, {
  memo, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import cl from 'classnames';
import PropTypes from 'prop-types';
import {connect, useDispatch} from 'react-redux';
import _ from 'lodash';
import TariffsPeriodSwitch from './TariffsPeriodSwitch';
import PERIODS from '../../constants/periods';
import {USER_ROLE} from '../../constants/user';
import ModalTariffDetails from './ModalTariffDetails/ModalTariffDetails';
import TariffsList from './TariffsList';
import TariffsTableViewList from './TariffsTableViewList';
import TariffsApi from '../../api/tariffs.api';
import './Tariffs.less';
import {NotificationError} from '../../services/notifications';
import ReferenceBooksApi from '../../api/reference-books.api';
import {buildRoute} from '../../configs/routes';
import URL_MAP from '../../constants/urls';
import ROLES from '../../constants/roles';
import {ActionRouteNavigate} from '../../store/actions/actions-route';
import TariffsRoleSwitch from './TariffsRoleSwitch';
import {t} from '../../services/i18nHandler';
import {LanguageContext} from '../../context/LanguageContext';

function Tariffs({
  loggedIn, landing, user,
}) {
  const language = useContext(LanguageContext);

  const dispatch = useDispatch();

  const role = useMemo(() => {
    const userRole = user.role;
    switch (userRole) {
    case USER_ROLE.EMPLOYER: return userRole;
    case USER_ROLE.RECRUITER: return userRole;
    default: return USER_ROLE.CANDIDATE;
    }
  }, [user]);

  const [tariffs, setTariffs] = useState([]);

  const [serviceOptions, setServiceOptions] = useState([]);

  const [activePeriod, setActivePeriod] = useState(PERIODS.ONE_MONTH.VALUE);

  const [activeRole, setActiveRole] = useState(role);

  const [detailsTariff, setDetailsTariff] = useState(null);

  const [demoDetails, setDemoDetails] = useState(false);

  const [tableView, setTableView] = useState(false);

  const detailsClick = useCallback((tariff, isDemo) => {
    if (isDemo) {
      setDetailsTariff(tariff);
      setDemoDetails(true);
    } else {
      setDetailsTariff(tariff);
    }
  }, []);

  const getAllTariffs = useCallback(async () => {
    await TariffsApi.getAllTariffs({filter: {promo: false}}).then((res) => {
      setTariffs(res);
    }).catch((error) => {
      NotificationError(error.error_description || error.message);
    });
  }, []);

  const fetchServiceOptions = useCallback(async () => {
    await ReferenceBooksApi.getReferenceBooksByKey('paid-service-options').then((res) => {
      if (res) {
        setServiceOptions(res.data);
      }
    }).catch((error) => {
      NotificationError(error.error_description || error.message);
    }).finally(() => {});
  }, []);

  const onCloseDetailsModal = useCallback(() => {
    setDetailsTariff(null);
    if (demoDetails) {
      setDemoDetails(false);
    }
  }, [demoDetails]);

  const roleName = useMemo(() => {
    switch (activeRole) {
    case 4: return ROLES.EMPLOYER;
    case 5: return ROLES.AGENCY;
    default: return ROLES.CANDIDATE;
    }
  }, [activeRole]);

  const onChangeParams = useCallback(() => {
    if (!landing) {
      const userRole = user.role;
      if (userRole === USER_ROLE.EMPLOYER) {
        dispatch(ActionRouteNavigate(buildRoute(URL_MAP.PLANS, ROLES.EMPLOYER, activePeriod)));
      } else if (userRole === USER_ROLE.RECRUITER) {
        dispatch(ActionRouteNavigate(buildRoute(URL_MAP.PLANS, ROLES.AGENCY, activePeriod)));
      } else {
        dispatch(ActionRouteNavigate(buildRoute(URL_MAP.PLANS, roleName, activePeriod)));
      }
    }
  }, [user, landing, activeRole, activePeriod, roleName]);

  const accessTariffs = useMemo(() => (_.filter(tariffs, {
    type: 1,
    roles: [activeRole],
  })), [tariffs, activeRole]);

  const serviceTariffs = useMemo(() => (_.filter(tariffs, {
    type: 2,
    roles: [activeRole],
  })), [tariffs, activeRole]);

  useEffect(() => {
    void getAllTariffs();
    void fetchServiceOptions();
  }, []);

  useEffect(() => {
    onChangeParams();
  }, [activeRole, activePeriod]);

  return (
    <div className="tariffs">
      {detailsTariff && (
        <ModalTariffDetails
          tariffData={detailsTariff}
          onClose={onCloseDetailsModal}
          isOpen
          showDemoDetails={demoDetails}
          canAddToCart={loggedIn && !demoDetails}
          options={serviceOptions}
        />
      )}
      <TariffsRoleSwitch
        onClick={setActiveRole}
        activeRole={activeRole}
        landing={landing}
        userRole={user.role}
      />
      <div className="tariffs_container">
        <div
          role="button"
          className="tariffs_tariffs-view_btn"
          onClick={() => setTableView(!tableView)}
        >
          <i className={cl('tariffs_tariffs-view_btn_icon', {
            table: tableView,
          })}
          />
          {tableView ? t('landingPage.tariffs.showAsTiles') : t('landingPage.tariffs.showAsTable')}
        </div>
        <TariffsPeriodSwitch onClick={setActivePeriod} activePeriod={activePeriod} />
      </div>
      {!tableView && (
        <TariffsList
          tariffs={accessTariffs}
          services={serviceTariffs}
          activePeriod={activePeriod}
          activeRole={activeRole}
          detailsClick={detailsClick}
          language={language}
          serviceOptions={serviceOptions}
        />
      )}
      {tableView && (
        <TariffsTableViewList
          tariffs={accessTariffs}
          services={serviceTariffs}
          activeRole={activeRole}
          activePeriod={activePeriod}
          detailsClick={detailsClick}
          language={language}
          serviceOptions={serviceOptions}
          landing={landing}
        />
      )}
    </div>
  );
}

Tariffs.defaultProps = {
  loggedIn: false,
  landing: false,
  user: {},
};

Tariffs.propTypes = {
  loggedIn: PropTypes.bool,
  landing: PropTypes.bool,
  user: PropTypes.shape({}),
};

function mapStateToProps({
  rSession, rUser,
}) {
  return {
    loggedIn: rSession.LOGGED_IN,
    user: rUser,
  };
}

export default connect(mapStateToProps, null)(memo(Tariffs));
